import { useRef, useState, type ChangeEvent } from "react"
import { IconChevronRight, IconDownload, IconTrash, IconUpload } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { extractApiError } from "@/lib/api-errors"
import { cn } from "@/lib/utils"
import { useAppDispatch } from "@/store"
import {
  ACCEPTED_DOCUMENT_TYPES,
  type BookingDocument,
  downloadBookingDocument,
  errorStatus,
  formatDocumentTime,
  formatFileSize,
  formatUploader,
  hidesDocumentsSection,
  scanStatusBadge,
  uploadBookingDocument,
  useDeleteBookingDocumentMutation,
  useGetBookingDocumentsQuery,
  visibleDocuments,
} from "../api/documentsApi"

/**
 * The booking's documents, inside the appointment dialog (AB#609): folded away until opened, then
 * each document's name, size, uploader and time, with its scan status while it is not yet cleared.
 * Upload adds a PDF or plain-text file; Download streams a cleared one; Remove asks first.
 *
 * Draws nothing for a booking CMS has no row for, and nothing when the list answers 404 (the
 * documents flag is off) or 403 (the caller neither hosts this booking nor manages appointments):
 * the server decides who may, so the section has no copy of that rule.
 *
 * An appointment recorded without scheduling (AB#868) gets no Upload: documents attach only to a
 * Booked appointment, so the server would refuse it. A muted line says so instead, and any documents
 * it already has still list.
 */
export function BookingDocumentsSection({ requestId, recordedWithoutScheduling = false }: {
  requestId?: string
  /** From `isRecordedWithoutScheduling`: no Upload, a note in its place. */
  recordedWithoutScheduling?: boolean
}) {
  if (!requestId) return null
  return <DocumentsForBooking key={requestId} requestId={requestId} recordedWithoutScheduling={recordedWithoutScheduling} />
}

function DocumentsForBooking({ requestId, recordedWithoutScheduling }: { requestId: string; recordedWithoutScheduling: boolean }) {
  const { data, error } = useGetBookingDocumentsQuery(requestId)
  const [deleteDocument] = useDeleteBookingDocumentMutation()
  const dispatch = useAppDispatch()
  const fileInput = useRef<HTMLInputElement>(null)

  const [open, setOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  // Nothing until the first answer: a viewer the server will refuse should not see the section
  // appear and vanish.
  if (hidesDocumentsSection(error)) return null
  if (!data && !error) return null

  const documents = data ? visibleDocuments(data.items) : []

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Cleared so choosing the same file again still fires a change.
    event.target.value = ""
    if (!file || uploading) return

    setUploading(true)
    setUploadError(null)
    const outcome = await uploadBookingDocument(requestId, file, dispatch)
    setUploading(false)
    if (outcome.ok) {
      setOpen(true)
    } else {
      setUploadError(outcome.message)
    }
  }

  async function download(document: BookingDocument) {
    if (downloadingId || !document.canDownload) return
    setDownloadingId(document.id)
    setRowError(null)
    const problem = await downloadBookingDocument(requestId, document)
    setDownloadingId(null)
    if (problem) setRowError(problem)
  }

  async function remove(document: BookingDocument) {
    if (removingId) return
    setRemovingId(document.id)
    setRowError(null)
    const result = await deleteDocument({ requestId, documentId: document.id })
    setRemovingId(null)
    setConfirmingId(null)
    if ("error" in result) {
      const failure = result.error as { status?: number | string; data?: unknown }
      setRowError(
        errorStatus(failure) === 403
          ? "You do not have permission to remove this document."
          : extractApiError(failure?.data) || "The document could not be removed.",
      )
    }
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section className="border-t pt-3" aria-labelledby="booking-documents-heading">
        <div className="flex items-center gap-2">
          <h3 id="booking-documents-heading" className="text-xs font-medium">
            <CollapsibleTrigger className="flex items-center gap-1">
              <IconChevronRight
                aria-hidden="true"
                className={cn("size-3.5 text-muted-foreground transition-transform", open && "rotate-90")}
              />
              Documents
              {data && <span className="font-normal text-muted-foreground">({documents.length})</span>}
            </CollapsibleTrigger>
          </h3>
          {data && !recordedWithoutScheduling && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept={ACCEPTED_DOCUMENT_TYPES}
                className="hidden"
                aria-label="Choose a document to upload"
                data-testid="booking-document-input"
                onChange={upload}
              />
              <Button
                size="xs"
                variant="ghost"
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
              >
                <IconUpload aria-hidden="true" />
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </>
          )}
        </div>
        {data && recordedWithoutScheduling && (
          <p className="mt-1 text-xs text-muted-foreground">
            Documents cannot be added to an appointment recorded without scheduling.
          </p>
        )}
        {uploadError && <p role="alert" className="mt-1 text-xs text-red-700">{uploadError}</p>}
        {error && <p role="alert" className="mt-1.5 text-xs text-red-700">The documents could not be loaded.</p>}

        <CollapsibleContent>
          {data && (
            <BookingDocumentsList
              documents={documents}
              onDownload={download}
              onRemove={document => { setRowError(null); setConfirmingId(document.id) }}
              onConfirmRemove={remove}
              onCancelRemove={() => setConfirmingId(null)}
              downloadingId={downloadingId}
              confirmingId={confirmingId}
              removingId={removingId}
            />
          )}
          {rowError && <p role="alert" className="mt-1 text-xs text-red-700">{rowError}</p>}
        </CollapsibleContent>
      </section>
    </Collapsible>
  )
}

/**
 * The list itself. Separate so it can be rendered and tested without a store. Without handlers it is
 * read-only; with them each row offers Download (only once the scan has cleared the document) and
 * Remove, which turns into a confirm step for the row named by `confirmingId`.
 */
export function BookingDocumentsList({
  documents,
  onDownload,
  onRemove,
  onConfirmRemove,
  onCancelRemove,
  downloadingId = null,
  confirmingId = null,
  removingId = null,
}: {
  documents: readonly BookingDocument[]
  onDownload?: (document: BookingDocument) => void
  onRemove?: (document: BookingDocument) => void
  onConfirmRemove?: (document: BookingDocument) => void
  onCancelRemove?: () => void
  downloadingId?: string | null
  confirmingId?: string | null
  removingId?: string | null
}) {
  const shown = visibleDocuments(documents)
  if (shown.length === 0) {
    return <p className="mt-1.5 text-xs text-muted-foreground">No documents yet.</p>
  }

  return (
    <ul className="mt-1.5 space-y-2">
      {shown.map(document => {
        const badge = scanStatusBadge(document.scanStatus)
        const confirming = confirmingId === document.id
        const downloadLabel = document.canDownload
          ? `Download ${document.fileName}`
          : "Download unavailable until the malware scan clears this document"
        return (
          <li key={document.id} className="rounded-md border px-2.5 py-2 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="min-w-0 flex-1 truncate font-medium" title={document.fileName}>{document.fileName}</span>
              {badge && <Badge variant={badge.variant} title={badge.title}>{badge.label}</Badge>}
              {onDownload && (
                <Button
                  size="icon-xs"
                  variant="ghost"
                  className="text-muted-foreground"
                  aria-label={downloadLabel}
                  title={downloadLabel}
                  disabled={!document.canDownload || downloadingId != null}
                  onClick={() => onDownload(document)}
                >
                  <IconDownload />
                </Button>
              )}
              {onRemove && !confirming && (
                <Button
                  size="icon-xs"
                  variant="ghost"
                  className="text-muted-foreground hover:text-red-700"
                  aria-label={`Remove ${document.fileName}`}
                  title="Remove document"
                  disabled={removingId != null}
                  onClick={() => onRemove(document)}
                >
                  <IconTrash />
                </Button>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <span>{formatFileSize(document.sizeBytes)}</span>
              <span aria-hidden="true">·</span>
              <span>{formatUploader(document.uploadedByUpn)}</span>
              <span aria-hidden="true">·</span>
              <time dateTime={document.uploadedAtUtc}>{formatDocumentTime(document.uploadedAtUtc)}</time>
            </div>
            {confirming && onConfirmRemove && (
              <div role="group" aria-label="Confirm removal" className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                <span>Remove this document? This cannot be undone.</span>
                <Button
                  size="xs"
                  variant="destructive"
                  disabled={removingId != null}
                  onClick={() => onConfirmRemove(document)}
                >
                  {removingId === document.id ? "Removing…" : "Remove"}
                </Button>
                <Button size="xs" variant="ghost" disabled={removingId != null} onClick={() => onCancelRemove?.()}>
                  Cancel
                </Button>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
